import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { AnalysisReport } from "../../OnboardingAPI";
import { formatDateTime, formatShare, formatSize, slotLabel } from "./leadLabels";

const CHECK_LABEL = { present: "Vorhanden", partial: "Teilweise", missing: "Fehlt" } as const;
const CHECK_VARIANT = { present: "success", partial: "warning", missing: "destructive" } as const;
const KIND_LABEL = { table: "Tabelle", plan: "Plan", document: "Dokument", image: "Bild", archive: "Archiv" } as const;

const delimiterLabel = (delimiter: string | undefined) =>
  delimiter === "\t" ? "Tab" : delimiter === undefined ? "-" : delimiter;

/**
 * The analysis report of a lead. Everything in it descends from the customer's uploads (file names,
 * column headers, findings that quote cells), so every value is rendered as React text, never as
 * HTML.
 */
export default function AnalysisReportView({ report }: { report: AnalysisReport }) {
  return (
    <div className="space-y-6" data-testid="analysis-report">
      <p className="text-xs text-muted-foreground">Erstellt {formatDateTime(report.generatedAt)}</p>

      <div>
        <h4 className="mb-2 text-sm font-semibold">Checkliste der Datenbereiche</h4>
        <ul className="space-y-2">
          {report.checklist.map((entry) => (
            <li key={entry.slot} className="rounded-lg border p-3" data-testid={`check-${entry.slot}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">
                  {entry.number}. {slotLabel(entry.slot)}
                </span>
                <Badge variant={CHECK_VARIANT[entry.state] ?? "outline"}>{CHECK_LABEL[entry.state] ?? entry.state}</Badge>
                <Badge variant={entry.required ? "default" : "secondary"}>{entry.required ? "Pflicht" : "Falls vorhanden"}</Badge>
              </div>
              {entry.findings.length > 0 && (
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                  {entry.findings.map((finding, index) => (
                    <li key={index}>{finding}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>

      {report.assetQuality && (
        <div>
          <h4 className="mb-2 text-sm font-semibold">Qualität der Anlagenliste</h4>
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="asset-quality">
            {([
              ["Zeilen", String(report.assetQuality.rows)],
              ["Doppelte IDs", String(report.assetQuality.duplicateIds)],
              // newer servers count ids that occur in more than one file apart from duplicates within a file
              ...(report.assetQuality.idsInMultipleFiles === undefined ? [] : [["IDs in mehreren Dateien", String(report.assetQuality.idsInMultipleFiles)]]),
              ["Leere IDs", String(report.assetQuality.emptyIds)],
              ["Generische Namen", formatShare(report.assetQuality.genericNameShare)],
            ] as [string, string][]).map(([label, value]) => (
              <div key={label} className="rounded-lg border p-3">
                <dt className="text-xs text-muted-foreground">{label}</dt>
                <dd className="mt-1 text-lg font-semibold tabular-nums">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <div>
        <h4 className="mb-2 text-sm font-semibold">Dateien</h4>
        <div className="space-y-3">
          {report.files.map((file) => (
            <div key={file.fileId} className="rounded-lg border p-3" data-testid={`report-file-${file.fileId}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="break-all text-sm font-medium">{file.originalName}</span>
                <Badge variant="outline">{slotLabel(file.slot)}</Badge>
                <Badge variant="secondary">{KIND_LABEL[file.kind] ?? file.kind}</Badge>
                <span className="text-xs text-muted-foreground">{formatSize(file.size)}</span>
              </div>
              {file.note && <p className="mt-2 text-sm text-muted-foreground">{file.note}</p>}
              {file.table && (
                <div className="mt-2">
                  <p className="text-xs text-muted-foreground">
                    {file.table.rowCount} Zeilen, Trennzeichen {delimiterLabel(file.table.delimiter)}, Kodierung{" "}
                    {file.table.encoding ?? "-"}
                  </p>
                  <Table className="mt-2">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Spalte</TableHead>
                        <TableHead>Erkannt als</TableHead>
                        <TableHead className="text-right">Befüllt</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {file.table.columns.map((column, index) => (
                        <TableRow key={index}>
                          <TableCell className="break-all">{column.header || "(ohne Titel)"}</TableCell>
                          <TableCell>{column.canonical ?? "-"}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatShare(column.filledShare)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          ))}
          {report.files.length === 0 && <p className="text-sm text-muted-foreground">Keine Dateien im Report.</p>}
        </div>
      </div>

      {report.answersSummary.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-semibold">Antworten im Überblick</h4>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {report.answersSummary.map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
